import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle, Loader2, Send, Clock, CreditCard, AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { generateOverrideOTP } from '@/app/services/voucherService'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'

interface UnpaidInvoiceOverrideModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (value: string, isOtp?: boolean) => void
  isLoading?: boolean
  errorType?: 'credit_limit_exceeded' | 'unpaid_invoices_exist'
  message?: string
  outstandingBalance?: number
  creditLimit?: number
  creditLimitSource?: 'party_specific' | 'organization_default' | 'none'
  unpaidInvoices?: Array<{
    voucher_number: string
    date: string
    amount: number
    payment_status: string
  }>
  partyName?: string
  partyId?: number
  error?: string
  overrideMethod?: 'pin' | 'otp'
  newInvoiceAmount?: number
  // Legacy props for backward compatibility
  unpaidCount?: number
  unpaidAmount?: number
}

export const UnpaidInvoiceOverrideModal: React.FC<UnpaidInvoiceOverrideModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  errorType,
  message,
  outstandingBalance,
  creditLimit,
  creditLimitSource,
  unpaidInvoices,
  partyName = 'Customer',
  partyId,
  error,
  overrideMethod = 'pin',
  newInvoiceAmount,
  // Legacy props
  unpaidCount,
  unpaidAmount
}) => {
  const [inputValue, setInputValue] = useState('')
  const [showError, setShowError] = useState(false)
  const [isOtpRequested, setIsOtpRequested] = useState(false)
  const [isRequestingOtp, setIsRequestingOtp] = useState(false)
  const [otpExpiryTime, setOtpExpiryTime] = useState<Date | null>(null)
  const [timeRemaining, setTimeRemaining] = useState('')

  // Use new props if available, otherwise fall back to legacy props
  const finalOutstandingBalance = outstandingBalance ?? unpaidAmount ?? 0
  const finalUnpaidCount = unpaidInvoices?.length ?? unpaidCount ?? 0
  const isCreditLimitExceeded = errorType === 'credit_limit_exceeded'

  // Calculate time remaining for OTP
  useEffect(() => {
    if (!otpExpiryTime) {
      setTimeRemaining('')
      return
    }

    const timer = setInterval(() => {
      const now = new Date()
      const diff = otpExpiryTime.getTime() - now.getTime()
      
      if (diff <= 0) {
        setTimeRemaining('Expired')
        setIsOtpRequested(false)
        setOtpExpiryTime(null)
        clearInterval(timer)
        return
      }

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`)
    }, 1000)

    return () => clearInterval(timer)
  }, [otpExpiryTime])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('')
      setShowError(false)
      setIsOtpRequested(false)
      setOtpExpiryTime(null)
      setTimeRemaining('')
    }
  }, [isOpen])

  const handleRequestOtp = useCallback(async () => {
    try {
      setIsRequestingOtp(true)
      const response = await generateOverrideOTP(partyId, newInvoiceAmount)

      if (response.success) {
        toast.success(response.message || 'OTP has been sent via Telegram')
        setIsOtpRequested(true)

        // Set expiry time
        const expiryTime = new Date()
        expiryTime.setMinutes(expiryTime.getMinutes() + (response.expires_in_minutes || 5))
        setOtpExpiryTime(expiryTime)
      }
    } catch (error: any) {
      console.error('Error requesting OTP:', error)
      toast.error(error?.response?.data?.detail || 'Failed to generate OTP. Please try again.')
    } finally {
      setIsRequestingOtp(false)
    }
  }, [partyId, newInvoiceAmount])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!inputValue.trim()) {
      setShowError(true)
      return
    }

    // For OTP mode, validate it's 6 digits
    if (overrideMethod === 'otp' && inputValue.length !== 6) {
      setShowError(true)
      toast.error('OTP must be exactly 6 digits')
      return
    }
    
    setShowError(false)
    onConfirm(inputValue, overrideMethod === 'otp')
  }

  const handleClose = () => {
    setInputValue('')
    setShowError(false)
    setIsOtpRequested(false)
    setOtpExpiryTime(null)
    onClose()
  }

  const getCreditLimitSourceLabel = (source?: string) => {
    switch (source) {
      case 'party_specific':
        return 'Party Credit Limit'
      case 'organization_default':
        return 'Default Credit Limit'
      case 'none':
      default:
        return 'No Credit Limit'
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCreditLimitExceeded ? (
              <>
                <CreditCard className="h-5 w-5 text-red-500" />
                Credit Limit Exceeded
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Unpaid Invoices Detected
              </>
            )}
          </DialogTitle>
          <DialogDescription className="pt-2 space-y-3">
            {message && (
              <p className="text-sm font-medium">{message}</p>
            )}
            
            {/* Credit Limit Information */}
            {isCreditLimitExceeded && creditLimit !== undefined && (
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-red-900 dark:text-red-100">Outstanding Balance:</span>
                  <span className="text-sm font-bold text-red-900 dark:text-red-100">
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                    }).format(finalOutstandingBalance)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-red-900 dark:text-red-100">Credit Limit:</span>
                  <span className="text-sm font-bold text-red-900 dark:text-red-100">
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                    }).format(creditLimit)}
                  </span>
                </div>
                {creditLimitSource && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-red-900 dark:text-red-100">Limit Type:</span>
                    <Badge variant="outline" className="text-xs">
                      {getCreditLimitSourceLabel(creditLimitSource)}
                    </Badge>
                  </div>
                )}
                <div className="border-t border-red-200 dark:border-red-800 pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-red-900 dark:text-red-100">Exceeds Limit By:</span>
                    <span className="text-sm font-bold text-red-900 dark:text-red-100">
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                      }).format(Math.max(0, finalOutstandingBalance - (creditLimit || 0)))}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Unpaid Invoices Information (for non-credit limit cases) */}
            {!isCreditLimitExceeded && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md space-y-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  {partyName} has outstanding unpaid invoices.
                </p>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  Unpaid Invoices: {finalUnpaidCount}
                </p>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  Total Outstanding: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                  }).format(finalOutstandingBalance)}
                </p>
              </div>
            )}

            {/* Show unpaid invoice details if available */}
            {unpaidInvoices && unpaidInvoices.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  View unpaid invoices ({unpaidInvoices.length})
                </summary>
                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                  {unpaidInvoices.map((invoice, idx) => (
                    <div key={idx} className="flex justify-between text-xs p-1 bg-muted/50 rounded">
                      <span>{invoice.voucher_number}</span>
                      <span>{new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(invoice.amount)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm">
                {overrideMethod === 'pin' 
                  ? 'To proceed with creating this invoice, please enter the override PIN.'
                  : 'To proceed with creating this invoice, please request and enter the OTP sent via Telegram.'}
              </AlertDescription>
            </Alert>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {overrideMethod === 'otp' && !isOtpRequested && (
            <div className="flex justify-center">
              <Button
                type="button"
                onClick={handleRequestOtp}
                disabled={isRequestingOtp}
                className="w-full"
              >
                {isRequestingOtp ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Requesting OTP...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Request OTP via Telegram
                  </>
                )}
              </Button>
            </div>
          )}

          {(overrideMethod === 'pin' || isOtpRequested) && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="override-input">
                    {overrideMethod === 'pin' ? 'Override PIN' : 'Enter OTP'}
                  </Label>
                  {timeRemaining && overrideMethod === 'otp' && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span className={timeRemaining === 'Expired' ? 'text-destructive' : ''}>
                        {timeRemaining}
                      </span>
                    </div>
                  )}
                </div>
                <Input
                  id="override-input"
                  type={overrideMethod === 'pin' ? 'password' : 'text'}
                  placeholder={overrideMethod === 'pin' ? 'Enter override PIN' : 'Enter 6-digit OTP'}
                  value={inputValue}
                  onChange={(e) => {
                    const value = e.target.value
                    // For OTP, only allow digits and max 6 characters
                    if (overrideMethod === 'otp') {
                      if (/^\d*$/.test(value) && value.length <= 6) {
                        setInputValue(value)
                      }
                    } else {
                      setInputValue(value)
                    }
                  }}
                  disabled={isLoading}
                  autoFocus
                  maxLength={overrideMethod === 'otp' ? 6 : 10}
                />
                {showError && (
                  <p className="text-sm text-destructive">
                    {overrideMethod === 'pin' 
                      ? 'Please enter the override PIN'
                      : 'Please enter a valid 6-digit OTP'}
                  </p>
                )}
              </div>

              {overrideMethod === 'otp' && timeRemaining === 'Expired' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRequestOtp}
                  disabled={isRequestingOtp}
                  className="w-full"
                >
                  {isRequestingOtp ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Requesting New OTP...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Request New OTP
                    </>
                  )}
                </Button>
              )}
            </>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {(overrideMethod === 'pin' || isOtpRequested) && (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !inputValue.trim() || (overrideMethod === 'otp' && timeRemaining === 'Expired')}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Override & Create Invoice'
                )}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}